// ── Map Vote Panel ── Casual/warmup map vote overlay (W-C-004 client caller).
//
// Talks to the GET/POST /api/map-vote endpoints registered in
// server/cmd/gameserver/main.go and implemented by
// HandleMapVote in server/internal/net/websocket.go.
//
// Server response shape (from game.GetVoteState):
//   { active: boolean, maps: string[], results: Record<string, number>,
//     timeRemaining: number, winner: string }
//
// This is the casual map vote, distinct from the competitive BO1 /api/veto
// flow — keep the two UIs separate.

import { getToken } from '../auth/token-store';
import { isMobile } from '../core/device';

const ACCENT = '#e94560';
const BG_DARK = 'rgba(0,0,0,0.92)';
const FONT = '"Courier New", monospace';

/** Server JSON shape — mirrors game.VoteState. */
export interface MapVoteState {
  active: boolean;
  maps: string[];
  results: Record<string, number>;
  timeRemaining: number;
  winner: string;
}

export class MapVotePanel {
  private overlay: HTMLElement;
  private contentEl: HTMLElement;
  private visible = false;
  private apiBaseUrl: string;
  private lastState: MapVoteState | null = null;
  private pollInterval: ReturnType<typeof setInterval> | null = null;
  // BUG-P-15: track which map the local player voted for so we can show
  // a visual highlight (.my-vote) on the matching card. This is set
  // optimistically on click and persists across server polls so the
  // highlight does not flicker between local and server state.
  private myVotedMap: string | null = null;

  constructor(serverUrl: string) {
    // Mirror LeaderboardPanel.buildFetchUrl: derive http(s) base from ws(s).
    this.apiBaseUrl = serverUrl
      .replace(/^wss:/, 'https:')
      .replace(/^ws:/, 'http:')
      .replace(/\/ws$/, '')
      .replace(/\/$/, '');

    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: BG_DARK,
      zIndex: '58',
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
      color: '#ffffff',
      userSelect: 'none',
    });

    const card = document.createElement('div');
    Object.assign(card.style, {
      width: '420px',
      maxWidth: '90vw',
      border: `2px solid ${ACCENT}40`,
      borderRadius: '8px',
      background: 'rgba(26,26,46,0.98)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    });

    const header = document.createElement('div');
    Object.assign(header.style, {
      padding: '20px 24px 14px',
      borderBottom: `1px solid ${ACCENT}30`,
      textAlign: 'center',
    });
    const title = document.createElement('div');
    Object.assign(title.style, {
      fontSize: '20px',
      fontWeight: 'bold',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      color: ACCENT,
      marginBottom: '4px',
    });
    title.textContent = 'MAP VOTE';
    header.appendChild(title);

    const subtitle = document.createElement('div');
    Object.assign(subtitle.style, {
      fontSize: '11px',
      color: '#555',
      letterSpacing: '2px',
    });
    subtitle.textContent = 'CAST YOUR VOTE';
    header.appendChild(subtitle);
    card.appendChild(header);

    this.contentEl = document.createElement('div');
    Object.assign(this.contentEl.style, {
      padding: '20px 24px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      minHeight: '120px',
    });
    card.appendChild(this.contentEl);

    const footer = document.createElement('div');
    Object.assign(footer.style, {
      padding: '10px',
      textAlign: 'center',
      fontSize: '11px',
      color: '#444',
      borderTop: '1px solid rgba(255,255,255,0.05)',
    });
    footer.textContent = '[M] Toggle  |  [ESC] Close';
    card.appendChild(footer);

    this.overlay.appendChild(card);

    this.overlay.addEventListener('click', (e: Event) => {
      if ((e as { target?: unknown }).target === this.overlay) this.hide();
    });

    document.body.appendChild(this.overlay);
  }

  async show(): Promise<void> {
    if (this.visible) return;
    this.visible = true;
    this.overlay.style.display = 'flex';
    this.startPolling();
    await this.poll();
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.overlay.style.display = 'none';
    this.stopPolling();
  }

  toggle(): void {
    if (this.visible) this.hide();
    else void this.show();
  }

  isOpen(): boolean {
    return this.visible;
  }

  dispose(): void {
    this.stopPolling();
    this.overlay.remove();
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollInterval = setInterval(() => {
      if (this.visible) void this.poll();
    }, 2000);
  }

  private stopPolling(): void {
    if (this.pollInterval === null) return;
    clearInterval(this.pollInterval);
    this.pollInterval = null;
  }

  /** Fetch the current vote state and render. Public so tests + callers can drive it. */
  async poll(): Promise<void> {
    try {
      const res = await fetch(`${this.apiBaseUrl}/api/map-vote`, {
        headers: this.authHeaders(),
      });
      if (!res.ok) {
        this.renderError(`Failed to load vote (HTTP ${res.status})`);
        return;
      }
      const state = (await res.json()) as MapVoteState;
      this.lastState = state;
      this.render(state);
    } catch (err) {
      this.renderError('Failed to load vote');
      // eslint-disable-next-line no-console
      console.error('[MapVote] poll failed:', err);
    }
  }

  // U-H-001 pattern: server requires auth; mirror skin-market.ts:23-28.
  private authHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  private async castVote(mapName: string): Promise<void> {
    // BUG-P-15: optimistically record local vote so the highlight renders
    // immediately on re-poll. Server has no per-voter readback in
    // MapVoteState, so this client field is the source of truth for the
    // "you voted for X" badge.
    this.myVotedMap = mapName;
    if (this.lastState) this.render(this.lastState);

    try {
      const res = await fetch(`${this.apiBaseUrl}/api/map-vote`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ mapName }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        this.renderError(`Vote failed: ${text || res.status}`);
        return;
      }
      // Re-poll to reflect the new tallies.
      await this.poll();
    } catch (err) {
      this.renderError('Vote failed');
      // eslint-disable-next-line no-console
      console.error('[MapVote] cast failed:', err);
    }
  }

  private render(state: MapVoteState): void {
    this.contentEl.innerHTML = '';
    const mobile = isMobile();

    if (!state.active) {
      const empty = document.createElement('div');
      Object.assign(empty.style, {
        textAlign: 'center',
        padding: '24px 0',
        color: '#888',
        fontSize: '13px',
        letterSpacing: '1px',
      });
      empty.textContent = 'No active map vote.';
      this.contentEl.appendChild(empty);
      return;
    }

    // Time remaining banner
    const timer = document.createElement('div');
    Object.assign(timer.style, {
      textAlign: 'center',
      fontSize: '12px',
      color: '#aaa',
      letterSpacing: '1px',
      marginBottom: '6px',
    });
    timer.textContent = `Time remaining: ${state.timeRemaining}s`;
    this.contentEl.appendChild(timer);

    for (const mapName of state.maps) {
      const tally = state.results[mapName] ?? 0;
      const isMyVote = this.myVotedMap === mapName;
      const btn = document.createElement('button');
      if (isMyVote) btn.className = 'my-vote';
      Object.assign(btn.style, {
        display: 'flex',
        flexDirection: mobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: mobile ? 'stretch' : 'center',
        position: 'relative',
        padding: '14px 54px 14px 16px',
        minHeight: '44px',
        background: isMyVote ? `${ACCENT}25` : 'rgba(60,60,50,0.4)',
        border: isMyVote ? `2px solid ${ACCENT}` : `1px solid ${ACCENT}30`,
        borderRadius: '4px',
        color: '#fff',
        fontFamily: FONT,
        fontSize: '14px',
        letterSpacing: '1px',
        cursor: 'pointer',
        textAlign: 'left',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
      });

      const preview = document.createElement('img');
      preview.src = `/maps/${encodeURIComponent(mapName)}/preview.jpg`;
      preview.alt = `${mapName} preview`;
      preview.loading = 'lazy';
      preview.decoding = 'async';
      Object.assign(preview.style, {
        display: 'block',
        width: mobile ? '100%' : '64px',
        maxWidth: '100%',
        height: mobile ? '76px' : '38px',
        objectFit: 'cover',
        borderRadius: '3px',
        margin: mobile ? '0 0 8px 0' : '0 10px 0 0',
        background: 'rgba(255,255,255,0.05)',
        flexShrink: '0',
      });
      preview.addEventListener('error', () => {
        preview.style.display = 'none';
      });
      btn.appendChild(preview);

      const name = document.createElement('span');
      name.textContent = mapName;
      Object.assign(name.style, {
        flex: '1',
        minWidth: '0',
      });
      btn.appendChild(name);

      const count = document.createElement('span');
      Object.assign(count.style, {
        position: 'absolute',
        top: '5px',
        right: '7px',
        minWidth: '18px',
        padding: '1px 5px',
        borderRadius: '999px',
        background: 'rgba(233,69,96,0.18)',
        border: `1px solid ${ACCENT}70`,
        color: ACCENT,
        fontSize: '10px',
        fontWeight: 'bold',
        lineHeight: '14px',
        textAlign: 'center',
        flexShrink: '0',
      });
      count.textContent = `${tally}`;
      btn.appendChild(count);

      // BUG-P-15: check icon overlay on the card the local player voted for.
      if (isMyVote) {
        const check = document.createElement('span');
        check.className = 'my-vote-check';
        Object.assign(check.style, {
          position: 'absolute',
          right: '10px',
          bottom: '7px',
          color: ACCENT,
          fontWeight: 'bold',
          fontSize: '16px',
        });
        check.textContent = '✓';
        btn.appendChild(check);
      }

      btn.addEventListener('click', () => {
        void this.castVote(mapName);
      });

      this.contentEl.appendChild(btn);
    }
  }

  private renderError(message: string): void {
    this.contentEl.innerHTML = '';
    const err = document.createElement('div');
    Object.assign(err.style, {
      textAlign: 'center',
      padding: '24px 0',
      color: '#ff6464',
      fontSize: '13px',
      letterSpacing: '1px',
    });
    err.textContent = message;
    this.contentEl.appendChild(err);
  }
}
